/**
 * DashboardCanvas — responsive grid layout for dashboard widgets.
 * Uses react-grid-layout v2 with useContainerWidth hook.
 */

import { useMemo, useCallback } from 'react'
import { Responsive, useContainerWidth, verticalCompactor } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import type { DashboardWidget } from '@/types/dashboard'
import type { WidgetDataBag } from './WidgetRenderer'
import { WidgetRenderer } from './WidgetRenderer'
import { WidgetWrapper } from './WidgetWrapper'
import { getWidgetEntry } from './widgetRegistry'

interface DashboardCanvasProps {
  widgets: DashboardWidget[]
  data: WidgetDataBag
  isEditMode: boolean
  rowHeight?: number
  onLayoutChange: (layouts: Array<{ id: string; x: number; y: number; w: number; h: number }>) => void
  onConfigureWidget?: (widgetId: string) => void
  onRemoveWidget?: (widgetId: string) => void
  onReviewAction?: (actionId: string, decision: 'approve' | 'dismiss') => void
}

export function DashboardCanvas({
  widgets,
  data,
  isEditMode,
  rowHeight = 80,
  onLayoutChange,
  onConfigureWidget,
  onRemoveWidget,
  onReviewAction,
}: DashboardCanvasProps) {
  const { containerRef, width } = useContainerWidth({ initialWidth: 1200 })

  // Build react-grid-layout layouts from widget definitions
  const layouts = useMemo(() => {
    const lg = widgets.map((w) => {
      const entry = getWidgetEntry(w.type)
      return {
        i: w.id,
        x: w.layout.x,
        y: w.layout.y,
        w: w.layout.w,
        h: w.layout.h,
        minW: entry.sizeConstraints.minW,
        minH: entry.sizeConstraints.minH,
        maxW: entry.sizeConstraints.maxW,
        maxH: entry.sizeConstraints.maxH,
      }
    })
    return { lg }
  }, [widgets])

  const handleLayoutChange = useCallback(
    (layout: readonly { readonly i: string; readonly x: number; readonly y: number; readonly w: number; readonly h: number }[]) => {
      onLayoutChange(
        layout.map((l) => ({
          id: l.i,
          x: l.x,
          y: l.y,
          w: l.w,
          h: l.h,
        })),
      )
    },
    [onLayoutChange],
  )

  if (widgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <p className="text-lg mb-2">No widgets yet</p>
        <p className="text-sm">Click "Add Widget" to build your dashboard</p>
      </div>
    )
  }

  return (
    <div ref={containerRef}>
      <Responsive
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
        cols={{ lg: 12, md: 8, sm: 4, xs: 2 }}
        rowHeight={rowHeight}
        width={width}
        dragConfig={{ enabled: isEditMode, handle: '.widget-drag-handle' }}
        resizeConfig={{ enabled: isEditMode }}
        compactor={verticalCompactor}
        onLayoutChange={handleLayoutChange}
        margin={[12, 12] as const}
        containerPadding={[0, 0] as const}
      >
        {widgets.map((widget) => (
          <div key={widget.id}>
            <WidgetWrapper
              widget={widget}
              isEditMode={isEditMode}
              onConfigure={onConfigureWidget}
              onRemove={onRemoveWidget}
            >
              <WidgetRenderer
                widget={widget}
                data={data}
                height={widget.layout.h * rowHeight - 64}
                onReviewAction={onReviewAction}
              />
            </WidgetWrapper>
          </div>
        ))}
      </Responsive>
    </div>
  )
}
